/**
 * Gallery Products Store
 * Manages product data, filtering, and display state
 */

import { GALLERY_CONFIG } from '../gallery-config.js';
import { ArrayUtils } from '../utils/array-utils.js';
import { filterByOrientation } from '../services/product/product-utils.js';
import { getDisplayFilterCount } from '../constants/filter-constants.js';

export function createGalleryProductsStore() {
  return {
    horizontal_products: [],
    vertical_products: [],

    filtered_products: [],
    has_active_filters: false,
    active_filter_count: 0,
    active_search_query: '',
    boost_active_filters: {},

    initial_load_complete: false,
    loading_horizontal: false,
    loading_vertical: false,
    loading_filtered: false,

    /**
     * Get current products based on state
     * Returns filtered products if filters/search active, otherwise returns products for current orientation
     * @returns {Array<Object>} Array of products to display
     */
    getCurrentProducts() {
      // Priority 1: Show filtered results when filters/search active
      if (this.has_active_filters && this.filtered_products.length > 0) {
        return this.filtered_products;
      }

      // Priority 2: Show orientation-specific products
      const core_store = Alpine.store('galleryCore');
      const orientation = core_store?.orientation || 'horizontal';

      return orientation === 'vertical'
        ? this.vertical_products
        : this.horizontal_products;
    },

    get filtered_artworks() {
      return this.getCurrentProducts();
    },

    /**
     * Get current count based on state
     * Matches getCurrentProducts() logic for consistency
     * @returns {number} Count of current products
     */
    getCurrentCount() {
      if (this.has_active_filters) {
        return this.filtered_products.length;
      }

      const core_store = Alpine.store('galleryCore');
      const orientation = core_store?.orientation || 'horizontal';

      return orientation === 'vertical'
        ? this.vertical_products.length
        : this.horizontal_products.length;
    },

    get artwork_count() {
      return this.getCurrentCount();
    },

    get works_count_display() {
      const count = this.getCurrentCount();
      return `${count} Works`;
    },

    get works_count_number() {
      return this.getCurrentCount();
    },


    get can_randomize() {
      const core_store = Alpine.store('galleryCore');
      return this.getCurrentProducts().length >= core_store.required_artwork_count;
    },


    get filter_count_display() {
      if (this.active_filter_count === 0) {
        return 'Filter';
      }

      return `${this.active_filter_count} Filter${this.active_filter_count !== 1 ? 's' : ''}`;
    },

    /**
     * Get boost filter count (alias for active_filter_count)
     * Used by filter badge in UI
     */
    get boost_filter_count() {
      return this.active_filter_count;
    },

    /**
     * Initial collection products load (before Boost loads orientation-specific)
     * This is called by gallery-builder-data.js during initial page load
     * Products are split by orientation tags or dimensions as fallback
     *
     * @param {Array<Object>} products - All products from collection
     */
    updateProducts(products) {
      if (!Array.isArray(products)) {
        console.warn('[ProductStore] updateProducts called with non-array:', products);
        return;
      }

      // If we already have products from Boost (which loads ALL products from the store),
      // don't overwrite with collection-limited products
      // Boost loads ~311 horizontal and ~321 vertical, collection only has ~115 and ~132
      if (this.horizontal_products.length > 200 || this.vertical_products.length > 200) {
        return;
      }

      // Also skip if initial_load_complete is set (Boost has finished)
      if (this.initial_load_complete) {
        return;
      }

      // Split products by orientation for initial load
      const horizontalProducts = [];
      const verticalProducts = [];

      products.forEach(product => {
        if (!product) return;

        const tags = Array.isArray(product.tags) ? product.tags : [];
        const hasVertical = tags.some(tag => tag && tag.includes('Vertical'));
        const hasHorizontal = tags.some(tag => tag && tag.includes('Horizontal'));

        if (hasVertical) {
          verticalProducts.push(product);
        } else if (hasHorizontal) {
          horizontalProducts.push(product);
        } else {
          // Fallback: check dimensions if no orientation tags
          if (product.featured_image?.width && product.featured_image?.height) {
            if (product.featured_image.width > product.featured_image.height) {
              horizontalProducts.push(product);
            } else {
              verticalProducts.push(product);
            }
          }
        }
      });

      // Only update if we don't already have products
      if (this.horizontal_products.length === 0 && this.vertical_products.length === 0) {
        this.horizontal_products = horizontalProducts;
        this.vertical_products = verticalProducts;
      }
    },

    /**
     * Set horizontal products (called during Boost initial load)
     * @param {Array<Object>} products - All horizontal products
     */
    setHorizontalProducts(products) {
      this.horizontal_products = products;
    },

    /**
     * Set vertical products (called during Boost initial load)
     * @param {Array<Object>} products - All vertical products
     */
    setVerticalProducts(products) {
      this.vertical_products = products;
    },

    /**
     * Set filtered products (when filters/search active)
     * @param {Array<Object>} products - Filtered products from Boost
     */
    setFilteredProducts(products) {
      this.filtered_products = products;
      this.has_active_filters = true;
      this.$dispatch && this.$dispatch('gallery:products-updated', { count: products.length });

      // Regenerate randomize queue with filtered products (clear immediately, generate in background)
      const queue_store = Alpine.store('galleryRandomizeQueue');
      if (queue_store) {
        queue_store.regenerateQueue(); // Don't await - runs in background
      }
    },

    /**
     * Updates active filter state
     * @param {Object} filters - Active filters keyed by category
     */
    updateActiveFilters(filters) {
      this.boost_active_filters = filters;
      this.active_filter_count = getDisplayFilterCount(filters);
      this.has_active_filters = this.active_filter_count > 0 || this.active_search_query.length > 0;
    },

    /**
     * Set search query state
     * @param {string} query - Search query
     */
    setSearchQuery(query) {
      this.active_search_query = query;
      this.has_active_filters = this.active_filter_count > 0 || query.length > 0;
    },

    /**
     * Clear all filters and search, revert to orientation products
     */
    clearFilters() {
      this.filtered_products = [];
      this.has_active_filters = false;
      this.active_filter_count = 0;
      this.active_search_query = '';
      this.boost_active_filters = {};

      this.$dispatch && this.$dispatch('gallery:filters-cleared');

      // Regenerate randomize queue with new product set (clear immediately, generate in background)
      const queue_store = Alpine.store('galleryRandomizeQueue');
      if (queue_store) {
        queue_store.regenerateQueue(); // Don't await - runs in background
      }
    },

    /**
     * Randomizes artwork selection using pre-randomized queue for instant performance
     * Falls back to synchronous randomization if queue is not ready
     *
     * @returns {Promise<void>}
     */
    async randomizeSelection() {
      const core_store = Alpine.store('galleryCore');
      const ui_store = Alpine.store('galleryUI');
      const variants_store = Alpine.store('galleryVariants');
      const queue_store = Alpine.store('galleryRandomizeQueue');

      if (!this.can_randomize) return;

      ui_store.randomizing = true;

      try {
        // Try to get pre-randomized selection from queue
        let randomized = queue_store.consumeNextSelection();

        // Fallback: If queue not ready, do synchronous randomization
        if (!randomized) {
          const available = this.getCurrentProducts();
          if (available.length < core_store.required_artwork_count) {
            throw new Error('Not enough artworks available for randomization');
          }
          randomized = ArrayUtils.randomizeSelection(available, core_store.required_artwork_count);

          // Queue will be regenerated in background after this completes
          setTimeout(() => queue_store.generateNextRandomSelection(), 100);
        }

        const { frame, mount, size, locked } = core_store.variant_selection;

        // Apply variants to artworks
        randomized.forEach((artwork, index) => {
          if (artwork) {
            if (locked) {
              variants_store.applyVariantToArtwork(artwork, index);
            } else {
              variants_store.applyPreviewVariantToArtwork(artwork, index);
            }
          }
        });

        // Select the new artworks
        core_store.selectArtworks(randomized);

        // Handle third artwork for duo layout
        if (core_store.required_artwork_count === GALLERY_CONFIG.LAYOUT.DUO_COUNT &&
            typeof core_store.setSavedThirdArtwork === 'function') {
          const available = this.getCurrentProducts();
          const saved_third = typeof core_store.getSavedThirdArtwork === 'function'
            ? core_store.getSavedThirdArtwork()
            : undefined;

          const selected_ids = randomized
            .filter(artwork => artwork && artwork.id)
            .map(artwork => artwork.id);

          if (saved_third && saved_third.id) {
            selected_ids.push(saved_third.id);
          }

          const third_candidate = ArrayUtils.randomSelectFromFiltered(
            available,
            selected_ids,
            GALLERY_CONFIG.LAYOUT.MIN_CAROUSEL_INDEX
          );

          if (third_candidate) {
            core_store.setSavedThirdArtwork(third_candidate);
            core_store.markArtworkImageDirty?.(third_candidate.id);
          }
        }

        // Update preview variants if needed
        if ((frame || mount) && !locked) {
          await variants_store.updatePreviewVariants(frame ? 'frame' : 'mount');
        }

        // Mark images as dirty to trigger reactive updates
        randomized.forEach(artwork => {
          if (artwork) {
            core_store.markArtworkImageDirty?.(artwork.id);
          }
        });

        // Save the current active artwork position before updating
        const wasCarouselOpen = ui_store.active_artwork_index !== null;
        const previousActiveIndex = ui_store.active_artwork_index;

        // Handle carousel state based on viewport
        if (window.innerWidth <= 990) {
          // Mobile: Always close carousel before re-opening
          ui_store.setActiveArtwork(null);
          setTimeout(() => {
            const firstMobileArtwork = document.querySelector('.gallery-builder__mobile-artwork-cell');
            if (firstMobileArtwork) {
              firstMobileArtwork.click();
            }
          }, 200);
        } else {
          // Desktop: Keep carousel open if it was open
          if (wasCarouselOpen && previousActiveIndex !== null) {
            const validPosition = Math.min(previousActiveIndex, randomized.length - 1);
            setTimeout(() => {
              ui_store.setActiveArtwork(validPosition);
            }, 100);
          }
        }

      } catch (error) {
        ui_store.setError(`Randomization failed: ${error.message}`);
      } finally {
        ui_store.randomizing = false;
      }
    },

    /**
     * Preloads images for the next potential randomization
     * Called automatically after each randomization to ensure instant subsequent randomizations
     *
     * @param {Object} core_store - Core gallery store
     * @param {Object} memory_store - Memory store for image caching
     * @param {Array} available - Available products to choose from
     * @param {Array} currentSelection - Currently selected products to exclude
     */
    async preloadNextRandomBatch(core_store, memory_store, available, currentSelection) {
      // Run in background - don't await
      Promise.resolve().then(async () => {
        try {
          // Get IDs of current selection to exclude them
          const currentIds = new Set(
            currentSelection
              .filter(artwork => artwork?.id)
              .map(artwork => artwork.id)
          );

          // Filter out current selection from available
          const othersAvailable = available.filter(
            artwork => artwork?.id && !currentIds.has(artwork.id)
          );

          // If not enough other artworks, use all available
          const poolToSample = othersAvailable.length >= core_store.required_artwork_count
            ? othersAvailable
            : available;

          // Sample more products for next randomization (up to 30)
          const sample_size = Math.min(30, poolToSample.length);
          const sample = [];
          const used_indices = new Set();

          while (sample.length < sample_size && sample.length < poolToSample.length) {
            const index = Math.floor(Math.random() * poolToSample.length);
            if (!used_indices.has(index)) {
              used_indices.add(index);
              sample.push(poolToSample[index]);
            }
          }

          // Collect URLs to preload
          const image_urls = new Set();
          const { frame, mount, locked } = core_store.variant_selection;

          sample.forEach(artwork => {
            if (!artwork) return;

            // Find the variant for this artwork
            let variant;
            if (locked && core_store.variant_manager) {
              variant = core_store.variant_manager.findVariant(artwork, core_store.variant_selection);
            } else if ((frame || mount) && core_store.variant_manager) {
              variant = core_store.variant_manager.findVariant(artwork, {
                ...core_store.variant_selection,
                mode: 'preview'
              });
            }

            // Collect image URLs
            const image_src = variant?.featured_image?.src || artwork.featured_image?.src;
            if (image_src) {
              const base_url = image_src.split('?')[0];
              // Desktop sizes
              image_urls.add(`${base_url}?width=630`);
              image_urls.add(`${base_url}?width=872`);
              image_urls.add(`${base_url}?width=160`);
              // Mobile sizes
              image_urls.add(`${base_url}?width=536`);
              image_urls.add(`${base_url}?width=360`);
              image_urls.add(`${base_url}?width=180`);
            }
          });

          const urls_array = Array.from(image_urls);

          // Filter out already cached images
          const uncached = urls_array.filter(url =>
            !memory_store.isImagePreloaded(url, core_store.orientation)
          );

          // Preload in background
          if (uncached.length > 0) {
            await memory_store.preloadImageBatch(uncached, core_store.orientation);
          }

        } catch (error) {
          // Silently fail - this is just an optimization
          console.debug('Background preload failed (non-critical):', error);
        }
      });
    }
  };
}
